import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/AuthContext';
import { Button } from '../ui/Button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/Card';
import { Input } from '../ui/Input';
import RichTextEditor from '../ui/RichTextEditor';
import { ArrowLeft, Plus, Edit, Trash2, Save, GripVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface QuestionOption {
  id: string;
  label: string;
  value: string;
}

type ScaleType = 'fill' | 'likert' | 'frequency';

interface Question {
  id: string;
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'radio' | 'checkbox' | 'number' | 'email' | 'url' | 'date' | 'range' | 'rating';
  title: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  scaleType?: ScaleType; // For rating type: 'fill' (0-100%), 'likert' (Strongly Disagree-Agree), 'frequency' (Never-Always)
  followUpQuestion?: string; // Optional follow-up question for rating types
  options?: QuestionOption[];
  order: number;
}

interface Subsection {
  id: string;
  title: string;
  description?: string;
  order: number;
  questions: Question[];
}

interface Section {
  id: string;
  title: string;
  description?: string;
  order: number;
  questions: Question[];
  subsections?: Subsection[];
}

interface EngagementPulseTemplate {
  id?: string;
  name: string;
  title: string;
  description?: string;
  version?: number;
  isActive?: boolean;
  sections: Section[];
  settings: {
    requireAllQuestions: boolean;
    showProgressBar: boolean;
    thankYouMessage?: string;
    allowSaveProgress: boolean;
  };
  metadata?: {
    category?: string;
    estimatedTimeMinutes?: number;
    frequency?: 'quarterly' | 'bi-annual' | 'annual' | 'custom';
  };
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
}

const EngagementPulseQuestions: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<EngagementPulseTemplate[]>([]);
  const [currentTemplate, setCurrentTemplate] = useState<EngagementPulseTemplate | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showTemplateForm, setShowTemplateForm] = useState(false);

  // Default template structure for engagement pulse checks
  const createDefaultTemplate = (): EngagementPulseTemplate => ({
    name: 'SDCO Engagement Pulse Check',
    title: 'Engagement Pulse Check',
    description: 'A brief self-reflection survey to assess your workplace engagement and support',
    version: 1,
    isActive: false,
    sections: [
      {
        id: `section_${Date.now()}_1`,
        title: 'Workplace Engagement',
        description: 'Questions about your work experience and support',
        order: 1,
        questions: [
          {
            id: `question_${Date.now()}_1`,
            type: 'radio',
            title: 'I know what is expected of me at work.',
            required: true,
            options: [
              { id: 'always', label: 'Always', value: 'always' },
              { id: 'sometimes', label: 'Sometimes', value: 'sometimes' },
              { id: 'never', label: 'Never', value: 'never' }
            ],
            order: 1
          },
          {
            id: `question_${Date.now()}_2`,
            type: 'radio',
            title: 'I have the materials, resources, tools, equipment, and support I need to do my best work.',
            required: true,
            options: [
              { id: 'always', label: 'Always', value: 'always' },
              { id: 'sometimes', label: 'Sometimes', value: 'sometimes' },
              { id: 'never', label: 'Never', value: 'never' }
            ],
            order: 2
          },
          {
            id: `question_${Date.now()}_3`,
            type: 'radio',
            title: 'In the past seven days, I have received recognition or praise for doing good work.',
            required: true,
            options: [
              { id: 'always', label: 'Always', value: 'always' },
              { id: 'sometimes', label: 'Sometimes', value: 'sometimes' },
              { id: 'never', label: 'Never', value: 'never' }
            ],
            order: 3
          },
          {
            id: `question_${Date.now()}_4`,
            type: 'radio',
            title: 'My manager, or someone at work, seems to care about me as a person.',
            required: true,
            options: [
              { id: 'always', label: 'Always', value: 'always' },
              { id: 'sometimes', label: 'Sometimes', value: 'sometimes' },
              { id: 'never', label: 'Never', value: 'never' }
            ],
            order: 4
          },
          {
            id: `question_${Date.now()}_5`,
            type: 'radio',
            title: 'There is someone at work who encourages my development.',
            required: true,
            options: [
              { id: 'always', label: 'Always', value: 'always' },
              { id: 'sometimes', label: 'Sometimes', value: 'sometimes' },
              { id: 'never', label: 'Never', value: 'never' }
            ],
            order: 5
          }
        ]
      }
    ],
    settings: {
      requireAllQuestions: false,
      showProgressBar: true,
      allowSaveProgress: true,
      thankYouMessage: 'Thank you for taking the time to share your thoughts. Your feedback helps us create a better workplace for everyone at SDCO.'
    },
    metadata: {
      category: 'engagement-pulse',
      estimatedTimeMinutes: 3,
      frequency: 'quarterly'
    }
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      
      // Fetch from Firestore
      const { collection, getDocs, orderBy, query } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      const templatesRef = collection(db, 'engagementPulseTemplates');
      const templatesQuery = query(templatesRef, orderBy('updatedAt', 'desc'));
      const snapshot = await getDocs(templatesQuery);
      
      const fetchedTemplates: EngagementPulseTemplate[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt || new Date(),
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt || new Date()
        };
      }) as EngagementPulseTemplate[];
      
      setTemplates(fetchedTemplates);
      
      // Set active template as current if exists
      const activeTemplate = fetchedTemplates.find(t => t.isActive);
      if (activeTemplate) {
        setCurrentTemplate(activeTemplate);
      }
      
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const saveTemplate = async (template: EngagementPulseTemplate) => {
    try {
      setSaving(true);
      
      const { collection, doc, setDoc, updateDoc, serverTimestamp } = await import('firebase/firestore');
      const { db } = await import('../../lib/firebase');
      
      // Clean up undefined values recursively
      const cleanObject = (obj: any): any => {
        if (obj === null || obj === undefined) return null;
        if (Array.isArray(obj)) {
          return obj.map(cleanObject);
        }
        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
              cleaned[key] = cleanObject(value);
            }
          }
          return cleaned;
        }
        return obj;
      };
      
      const templateData = cleanObject({
        ...template,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.uid
      });
      
      if (template.id) {
        // Update existing template
        const templateRef = doc(db, 'engagementPulseTemplates', template.id);
        await updateDoc(templateRef, templateData);
      } else {
        // Create new template
        const templateRef = doc(collection(db, 'engagementPulseTemplates'));
        await setDoc(templateRef, {
          ...templateData,
          createdAt: serverTimestamp(),
          createdBy: currentUser?.uid
        });
        template.id = templateRef.id;
      }
      
      // Update local state
      setTemplates(prev => {
        const index = prev.findIndex(t => t.id === template.id);
        if (index >= 0) {
          const updated = [...prev];
          updated[index] = template;
          return updated;
        } else {
          return [...prev, template];
        }
      });
      
      setCurrentTemplate(template);
      setError('');
      
    } catch (err: any) {
      console.error('Error saving template:', err);
      setError('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const createNewTemplate = () => {
    const newTemplate = createDefaultTemplate();
    setCurrentTemplate(newTemplate);
    setShowTemplateForm(true);
  };

  const addSection = () => {
    if (!currentTemplate) return;
    
    const newSection: Section = {
      id: `section_${Date.now()}`,
      title: 'New Section',
      description: '',
      order: currentTemplate.sections.length + 1,
      questions: []
    };
    
    setCurrentTemplate({
      ...currentTemplate,
      sections: [...currentTemplate.sections, newSection]
    });
  };

  const deleteSection = (sectionId: string) => {
    if (!currentTemplate) return;
    if (!window.confirm('Are you sure you want to delete this section and all its questions?')) return;
    
    const updatedSections = currentTemplate.sections
      .filter(s => s.id !== sectionId)
      .map((s, index) => ({ ...s, order: index + 1 }));
    
    setCurrentTemplate({
      ...currentTemplate,
      sections: updatedSections
    });
  };

  const updateSection = (sectionId: string, updates: Partial<Section>) => {
    if (!currentTemplate) return;
    
    const updatedSections = currentTemplate.sections.map(section =>
      section.id === sectionId ? { ...section, ...updates } : section
    );
    
    setCurrentTemplate({
      ...currentTemplate,
      sections: updatedSections
    });
  };

  const addQuestion = (sectionId: string) => {
    if (!currentTemplate) return;
    
    const section = currentTemplate.sections.find(s => s.id === sectionId);
    if (!section) return;
    
    const newQuestion: Question = {
      id: `question_${Date.now()}`,
      type: 'text',
      title: 'New Question',
      description: '',
      required: false,
      order: section.questions.length + 1
    };
    
    const updatedSections = currentTemplate.sections.map(s =>
      s.id === sectionId
        ? { ...s, questions: [...s.questions, newQuestion] }
        : s
    );
    
    setCurrentTemplate({
      ...currentTemplate,
      sections: updatedSections
    });
    
    setEditingQuestion(newQuestion);
  };

  const updateQuestion = (sectionId: string, questionId: string, updates: Partial<Question>) => {
    if (!currentTemplate) return;
    
    const updatedSections = currentTemplate.sections.map(section =>
      section.id === sectionId
        ? {
            ...section,
            questions: section.questions.map(q =>
              q.id === questionId ? { ...q, ...updates } : q
            )
          }
        : section
    );
    
    setCurrentTemplate({
      ...currentTemplate,
      sections: updatedSections
    });
  };

  const deleteQuestion = (sectionId: string, questionId: string) => {
    if (!currentTemplate) return;
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    const updatedSections = currentTemplate.sections.map(section =>
      section.id === sectionId
        ? {
            ...section,
            questions: section.questions
              .filter(q => q.id !== questionId)
              .map((q, index) => ({ ...q, order: index + 1 }))
          }
        : section
    );
    
    setCurrentTemplate({
      ...currentTemplate,
      sections: updatedSections
    });
    
    if (editingQuestion?.id === questionId) {
      setEditingQuestion(null);
    }
  };

  const moveQuestion = (sectionId: string, questionId: string, direction: 'up' | 'down') => {
    if (!currentTemplate) return;
    
    const section = currentTemplate.sections.find(s => s.id === sectionId);
    if (!section) return;
    
    const questions = [...section.questions].sort((a, b) => a.order - b.order);
    const currentIndex = questions.findIndex(q => q.id === questionId);
    
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === questions.length - 1)
    ) {
      return;
    }
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    [questions[currentIndex], questions[newIndex]] = [questions[newIndex], questions[currentIndex]];
    
    // Update order numbers
    const updatedQuestions = questions.map((q, index) => ({ ...q, order: index + 1 }));
    
    const updatedSections = currentTemplate.sections.map(s =>
      s.id === sectionId
        ? { ...s, questions: updatedQuestions }
        : s
    );
    
    setCurrentTemplate({
      ...currentTemplate,
      sections: updatedSections
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin')} className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Admin
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-primary">
                Engagement Pulse Questions
              </h1>
              <p className="text-muted-foreground">Create and manage self-reflection surveys for team members</p>
            </div>
          </div>
          <Button onClick={createNewTemplate} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            New Template
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {error && (
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Templates List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Templates ({templates.length})</CardTitle>
                <CardDescription>Manage your engagement pulse templates</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                        currentTemplate?.id === template.id
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => setCurrentTemplate(template)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{template.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {template.sections.reduce((total, section) => total + section.questions.length, 0)} questions in {template.sections.length} sections
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {template.metadata?.frequency} • {template.metadata?.estimatedTimeMinutes}min
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {template.isActive && (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                              Active
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {templates.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      <p>No templates yet</p>
                      <p className="text-sm">Create your first template</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Template Editor */}
          <div className="lg:col-span-2">
            {currentTemplate ? (
              <div className="space-y-6">
                {/* Template Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle>Template Settings</CardTitle>
                    <CardDescription>Configure your engagement pulse template</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Template Name</label>
                        <Input
                          value={currentTemplate.name}
                          onChange={(e) => setCurrentTemplate({
                            ...currentTemplate,
                            name: e.target.value
                          })}
                          placeholder="Template name"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Title</label>
                        <Input
                          value={currentTemplate.title}
                          onChange={(e) => setCurrentTemplate({
                            ...currentTemplate,
                            title: e.target.value
                          })}
                          placeholder="Survey title"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Description</label>
                      <RichTextEditor
                        value={currentTemplate.description || ''}
                        onChange={(value) => setCurrentTemplate({
                          ...currentTemplate,
                          description: value
                        })}
                        placeholder="Describe the purpose of this engagement survey"
                        className="border border-input rounded-md"
                      />
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium">Template Options</h4>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={currentTemplate.isActive}
                              onChange={(e) => setCurrentTemplate({
                                ...currentTemplate,
                                isActive: e.target.checked
                              })}
                              className="rounded border-input"
                            />
                            <span className="text-sm">Active Template</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={currentTemplate.settings.requireAllQuestions || false}
                              onChange={(e) => setCurrentTemplate({
                                ...currentTemplate,
                                settings: {
                                  ...currentTemplate.settings,
                                  requireAllQuestions: e.target.checked
                                }
                              })}
                              className="rounded border-input"
                            />
                            <span className="text-sm">Require All Questions</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={currentTemplate.settings.showProgressBar || false}
                              onChange={(e) => setCurrentTemplate({
                                ...currentTemplate,
                                settings: {
                                  ...currentTemplate.settings,
                                  showProgressBar: e.target.checked
                                }
                              })}
                              className="rounded border-input"
                            />
                            <span className="text-sm">Show Progress Bar</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={currentTemplate.settings.allowSaveProgress || false}
                              onChange={(e) => setCurrentTemplate({
                                ...currentTemplate,
                                settings: {
                                  ...currentTemplate.settings,
                                  allowSaveProgress: e.target.checked
                                }
                              })}
                              className="rounded border-input"
                            />
                            <span className="text-sm">Allow Save Progress</span>
                          </label>
                        </div>
                        
                        <div className="space-y-3">
                          <h4 className="text-sm font-medium">Survey Metadata</h4>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Frequency</label>
                            <select
                              value={currentTemplate.metadata?.frequency || 'quarterly'}
                              onChange={(e) => setCurrentTemplate({
                                ...currentTemplate,
                                metadata: {
                                  ...currentTemplate.metadata,
                                  frequency: e.target.value as 'quarterly' | 'bi-annual' | 'annual' | 'custom'
                                }
                              })}
                              className="w-full px-3 py-2 border border-input rounded-md bg-background"
                            >
                              <option value="quarterly">Quarterly</option>
                              <option value="bi-annual">Bi-Annual</option>
                              <option value="annual">Annual</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Estimated Time (minutes)</label>
                            <Input
                              type="number"
                              value={currentTemplate.metadata?.estimatedTimeMinutes || 10}
                              onChange={(e) => setCurrentTemplate({
                                ...currentTemplate,
                                metadata: {
                                  ...currentTemplate.metadata,
                                  estimatedTimeMinutes: parseInt(e.target.value) || 10
                                }
                              })}
                              min="1"
                              max="60"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Thank You Message</label>
                        <RichTextEditor
                          value={currentTemplate.settings.thankYouMessage || ''}
                          onChange={(value) => setCurrentTemplate({
                            ...currentTemplate,
                            settings: {
                              ...currentTemplate.settings,
                              thankYouMessage: value
                            }
                          })}
                          placeholder="Message shown after completing the survey"
                        />
                      </div>
                      
                      <div className="flex justify-end">
                        <Button 
                          onClick={() => saveTemplate(currentTemplate)} 
                          disabled={saving}
                          className="flex items-center gap-2"
                        >
                          <Save className="h-4 w-4" />
                          {saving ? 'Saving...' : 'Save Template'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Sections and Questions */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Survey Sections</h3>
                    <Button 
                      onClick={addSection} 
                      size="sm" 
                      className="flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" />
                      Add Section
                    </Button>
                  </div>
                  
                  {currentTemplate.sections
                    .sort((a, b) => a.order - b.order)
                    .map((section) => (
                    <Card key={section.id}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <CardTitle className="text-lg">
                                Section {section.order}
                              </CardTitle>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => deleteSection(section.id)}
                                className="flex items-center gap-1 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-3 w-3" />
                                Delete Section
                              </Button>
                            </div>
                            
                            <div className="space-y-3">
                              <div>
                                <label className="text-sm font-medium">Section Title</label>
                                <Input
                                  value={section.title}
                                  onChange={(e) => updateSection(section.id, { title: e.target.value })}
                                  placeholder="Section title"
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <label className="text-sm font-medium">Section Description</label>
                                <Input
                                  value={section.description || ''}
                                  onChange={(e) => updateSection(section.id, { description: e.target.value })}
                                  placeholder="Brief description of this section"
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            
                            <CardDescription className="mt-3">
                              {section.questions.length} questions in this section
                            </CardDescription>
                          </div>
                          <Button 
                            onClick={() => addQuestion(section.id)} 
                            size="sm" 
                            className="flex items-center gap-2 ml-4"
                          >
                            <Plus className="h-4 w-4" />
                            Add Question
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {section.questions
                            .sort((a, b) => a.order - b.order)
                            .map((question, questionIndex) => (
                            <div
                              key={question.id}
                              className={`p-4 border rounded-lg ${
                                editingQuestion?.id === question.id
                                  ? 'border-primary bg-primary/5'
                                  : 'hover:bg-muted/50'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    {section.order}.{questionIndex + 1}
                                  </span>
                                  <span className={`px-2 py-1 text-xs rounded-full ${
                                    question.type === 'rating' ? 'bg-blue-100 text-blue-800' :
                                    question.type === 'textarea' ? 'bg-green-100 text-green-800' :
                                    question.type === 'radio' ? 'bg-purple-100 text-purple-800' :
                                    'bg-secondary text-secondary-foreground'
                                  }`}>
                                    {question.type}
                                  </span>
                                  {question.required && (
                                    <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                                      Required
                                    </span>
                                  )}
                                </div>
                                
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => moveQuestion(section.id, question.id, 'up')}
                                    disabled={questionIndex === 0}
                                  >
                                    ↑
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => moveQuestion(section.id, question.id, 'down')}
                                    disabled={questionIndex === section.questions.length - 1}
                                  >
                                    ↓
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setEditingQuestion(question)}
                                    className="flex items-center gap-1"
                                  >
                                    <Edit className="h-3 w-3" />
                                    Edit
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => deleteQuestion(section.id, question.id)}
                                    className="flex items-center gap-1 text-destructive hover:text-destructive"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                    Delete
                                  </Button>
                                </div>
                              </div>
                              
                              <div>
                                <p className="font-medium">{question.title}</p>
                                {question.description && (
                                  <div 
                                    className="text-sm text-muted-foreground mt-1 prose prose-sm max-w-none"
                                    dangerouslySetInnerHTML={{ __html: question.description }}
                                  />
                                )}
                                {question.type === 'rating' && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Scale: {question.scaleType === 'likert' ? 'Likert (Strongly Disagree - Strongly Agree)' :
                                           question.scaleType === 'frequency' ? 'Frequency (Never - Always)' :
                                           'Fill (0% - 100%)'}
                                  </p>
                                )}
                                {question.options && question.options.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-muted-foreground mb-1">Options:</p>
                                    <div className="flex flex-wrap gap-1">
                                      {question.options.map(option => (
                                        <span key={option.id} className="px-2 py-1 text-xs bg-muted rounded">
                                          {option.label}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                          
                          {section.questions.length === 0 && (
                            <div className="text-center py-8 text-muted-foreground">
                              <Plus className="h-12 w-12 mx-auto mb-4 opacity-50" />
                              <p>No questions in this section yet</p>
                              <p className="text-sm">Add your first question to get started</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  
                  {currentTemplate.sections.length === 0 && (
                    <Card>
                      <CardContent className="text-center py-12">
                        <h3 className="text-lg font-medium mb-2">No Sections Yet</h3>
                        <p className="text-muted-foreground mb-4">
                          Add sections to organize your engagement pulse questions.
                        </p>
                        <Button onClick={addSection}>
                          Add First Section
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            ) : (
              <Card>
                <CardContent className="text-center py-12">
                  <h3 className="text-lg font-medium mb-2">No Template Selected</h3>
                  <p className="text-muted-foreground mb-4">
                    Select a template from the list or create a new one to get started.
                  </p>
                  <Button onClick={createNewTemplate}>
                    Create New Template
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Question Editor Modal */}
        {editingQuestion && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle>Edit Question</CardTitle>
                <CardDescription>Configure question settings and options</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Question Title</label>
                  <Input
                    value={editingQuestion.title}
                    onChange={(e) => setEditingQuestion({
                      ...editingQuestion,
                      title: e.target.value
                    })}
                    placeholder="Enter question title"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Description (Optional)</label>
                  <RichTextEditor
                    value={editingQuestion.description || ''}
                    onChange={(value) => setEditingQuestion({
                      ...editingQuestion,
                      description: value
                    })}
                    placeholder="Additional context or instructions"
                    className="border border-input rounded-md"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Question Type</label>
                    <select
                      value={editingQuestion.type}
                      onChange={(e) => {
                        const newType = e.target.value as Question['type'];
                        const updatedQuestion = {
                          ...editingQuestion,
                          type: newType
                        };
                        
                        // Clear incompatible fields when changing question type
                        if (!['radio', 'checkbox', 'select', 'multiselect'].includes(newType)) {
                          // If switching away from option-based types, clear options
                          updatedQuestion.options = undefined;
                        }
                        
                        if (newType !== 'rating') {
                          // If switching away from rating type, clear scaleType
                          updatedQuestion.scaleType = undefined;
                        } else {
                          // If switching to rating type, set default scaleType
                          updatedQuestion.scaleType = updatedQuestion.scaleType || 'fill';
                        }
                        
                        if (!['text', 'textarea'].includes(newType)) {
                          // If switching away from text types, clear placeholder
                          updatedQuestion.placeholder = undefined;
                        }
                        
                        setEditingQuestion(updatedQuestion);
                      }}
                      className="w-full px-3 py-2 border border-input rounded-md bg-background"
                    >
                      <option value="text">Short Text</option>
                      <option value="textarea">Long Text</option>
                      <option value="rating">Rating Scale</option>
                      <option value="radio">Multiple Choice</option>
                      <option value="checkbox">Checkboxes</option>
                      <option value="select">Dropdown</option>
                      <option value="number">Number</option>
                      <option value="email">Email</option>
                      <option value="date">Date</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editingQuestion.required}
                        onChange={(e) => setEditingQuestion({
                          ...editingQuestion,
                          required: e.target.checked
                        })}
                        className="rounded border-input"
                      />
                      <span className="text-sm font-medium">Required Question</span>
                    </label>
                  </div>
                </div>

                {editingQuestion.type === 'rating' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Scale Type</label>
                      <select
                        value={editingQuestion.scaleType || 'fill'}
                        onChange={(e) => setEditingQuestion({
                          ...editingQuestion,
                          scaleType: e.target.value as ScaleType
                        })}
                        className="w-full px-3 py-2 border border-input rounded-md bg-background"
                      >
                        <option value="fill">Fill (0% - 100%)</option>
                        <option value="likert">Likert (Strongly Disagree - Strongly Agree)</option>
                        <option value="frequency">Frequency (Never - Always)</option>
                      </select>
                      <p className="text-xs text-muted-foreground">
                        {editingQuestion.scaleType === 'likert' && 'Options: Strongly Disagree, Disagree, Neutral, Agree, Strongly Agree'}
                        {editingQuestion.scaleType === 'frequency' && 'Options: Never, Rarely, Sometimes, Often, Always'}
                        {(!editingQuestion.scaleType || editingQuestion.scaleType === 'fill') && 'A slider from 0% to 100%'}
                      </p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Follow-up Question (Optional)</label>
                      <Input
                        value={editingQuestion.followUpQuestion || ''}
                        onChange={(e) => setEditingQuestion({
                          ...editingQuestion,
                          followUpQuestion: e.target.value
                        })}
                        placeholder="e.g., What factors influenced your response?"
                      />
                      <p className="text-xs text-muted-foreground">
                        This question will appear below the rating scale with a text area for the response.
                      </p>
                    </div>
                  </div>
                )}

                {(editingQuestion.type === 'text' || editingQuestion.type === 'textarea') && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Placeholder Text</label>
                    <Input
                      value={editingQuestion.placeholder || ''}
                      onChange={(e) => setEditingQuestion({
                        ...editingQuestion,
                        placeholder: e.target.value
                      })}
                      placeholder="Placeholder text for the input"
                    />
                  </div>
                )}

                {['radio', 'checkbox', 'select', 'multiselect'].includes(editingQuestion.type) && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Options</label>
                    <div className="space-y-2">
                      {(editingQuestion.options || []).map((option, index) => (
                        <div key={option.id} className="flex items-center gap-2">
                          <Input
                            value={option.label}
                            onChange={(e) => {
                              const updatedOptions = [...(editingQuestion.options || [])];
                              updatedOptions[index] = { ...option, label: e.target.value, value: e.target.value };
                              setEditingQuestion({
                                ...editingQuestion,
                                options: updatedOptions
                              });
                            }}
                            placeholder={`Option ${index + 1}`}
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const updatedOptions = (editingQuestion.options || []).filter((_, i) => i !== index);
                              setEditingQuestion({
                                ...editingQuestion,
                                options: updatedOptions
                              });
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newOption: QuestionOption = {
                            id: `option_${Date.now()}`,
                            label: '',
                            value: ''
                          };
                          setEditingQuestion({
                            ...editingQuestion,
                            options: [...(editingQuestion.options || []), newOption]
                          });
                        }}
                        className="flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Add Option
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={() => {
                      // Find which section this question belongs to
                      const section = currentTemplate?.sections.find(s => 
                        s.questions.some(q => q.id === editingQuestion.id)
                      );
                      if (section) {
                        updateQuestion(section.id, editingQuestion.id, editingQuestion);
                        setEditingQuestion(null);
                      }
                    }}
                    className="flex items-center gap-2"
                  >
                    <Save className="h-4 w-4" />
                    Save Question
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setEditingQuestion(null)}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default EngagementPulseQuestions;
